import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2, Users, Briefcase, Calendar, ShieldCheck, MapPin, ArrowLeft, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Stars } from "@/components/Stars";
import { api } from "@/lib/api";
import { gbp, loadOffer, saveOffer } from "@/lib/constants";

export default function TaxiDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => { api.get(`/vehicles/${id}`).then(({ data }) => setData(data)).catch(() => {}); }, [id]);

  if (!data) return <div className="flex h-96 items-center justify-center"><Loader2 className="animate-spin text-brand" size={32} /></div>;
  const { vehicle: v, operator: op, reviews } = data;

  const book = () => {
    const offer = loadOffer();
    saveOffer({
      offer_id: v.id, vehicle_id: v.id, operator_name: v.operator_name, operator_id: v.operator_id,
      driver_id: v.driver_id, driver_name: v.driver_name, vehicle_key: v.vehicle_key, vehicle_label: v.vehicle_label,
      title: `${v.vehicle_label} · ${v.make} ${v.model}`, rating: v.rating, trips: v.trips, pax: v.pax, bags: v.bags,
      features: v.features, image: v.image,
      total: offer?.total || 0, fare: offer?.fare || 0, duration_minutes: offer?.duration_minutes || 30, pickup_eta_min: offer?.pickup_eta_min || 8,
    });
    navigate("/book");
  };

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-8">
      <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="mb-4 gap-1" data-testid="back-btn"><ArrowLeft size={16} /> Back</Button>
      <div className="grid gap-8 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <div className="overflow-hidden rounded-2xl border border-slate-200">
            <img src={v.image} alt={v.vehicle_label} className="h-80 w-full object-cover" />
          </div>
          <div className="mt-6">
            <div className="flex flex-wrap items-center gap-2">
              {v.executive && <Badge className="bg-navy text-white">Executive</Badge>}
              {v.wheelchair && <Badge className="bg-emerald-600 text-white">Wheelchair Accessible</Badge>}
              {v.airport_specialist && <Badge variant="outline">Airport Specialist</Badge>}
              {v.available_247 && <Badge variant="outline">24/7</Badge>}
            </div>
            <h1 className="mt-3 font-heading text-3xl font-bold text-navy">{v.make} {v.model}</h1>
            <p className="mt-1 text-slate-500">{v.vehicle_label} · {v.color} · {v.year} · {v.reg}</p>
            <div className="mt-2 flex items-center gap-2"><Stars rating={v.rating} /><span className="font-semibold text-navy">{v.rating}/5</span><span className="text-sm text-slate-400">· {v.trips.toLocaleString()} trips</span></div>

            <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3">
              {[[Users, `${v.pax} passengers`], [Briefcase, `${v.bags} large bags`], [Calendar, `${v.years_operating} years operating`]].map(([Icon, t]) => (
                <div key={t} className="flex items-center gap-2 rounded-xl border border-slate-200 p-3 text-sm text-slate-700"><Icon size={16} className="text-brand" /> {t}</div>
              ))}
            </div>

            <h3 className="mt-8 font-heading text-lg font-semibold text-navy">Features</h3>
            <div className="mt-3 flex flex-wrap gap-2">
              {v.features.map((f) => <span key={f} className="rounded-full bg-slate-100 px-3 py-1.5 text-sm text-slate-700">{f}</span>)}
            </div>

            {op && <>
              <h3 className="mt-8 font-heading text-lg font-semibold text-navy">Service areas</h3>
              <div className="mt-3 flex flex-wrap gap-2">{op.service_areas.map((s) => <span key={s} className="flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1.5 text-sm text-slate-600"><MapPin size={13} className="text-brand" /> {s}</span>)}</div>
            </>}

            <h3 className="mt-8 font-heading text-lg font-semibold text-navy">Recent reviews</h3>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              {reviews.map((r) => (
                <div key={r.id} className="rounded-xl border border-slate-200 p-4">
                  <div className="flex items-center justify-between"><span className="text-sm font-semibold text-navy">{r.customer_name}</span><Stars rating={r.rating} /></div>
                  <p className="mt-2 text-sm text-slate-600">"{r.text}"</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div>
          <div className="sticky top-24 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <p className="text-sm text-slate-500">Operator</p>
            <p className="font-heading text-lg font-semibold text-navy">{v.operator_name}</p>
            <div className="my-4 h-px bg-slate-200" />
            {loadOffer()?.total ? <>
              <p className="text-sm text-slate-500">Estimated fare</p>
              <p className="font-heading text-3xl font-bold text-navy">{gbp(loadOffer().total)}</p>
            </> : <p className="text-sm text-slate-500">Start a search to see your fare for this vehicle.</p>}
            <Button className="mt-5 w-full bg-brand hover:bg-brand/90" onClick={book} data-testid="book-this-taxi">Book This Taxi</Button>
            <Button variant="outline" className="mt-2 w-full gap-2" onClick={() => navigate("/help")} data-testid="ask-question"><MessageSquare size={15} /> Ask a Question</Button>
            <p className="mt-4 flex items-center gap-2 text-xs text-slate-400"><ShieldCheck size={14} className="text-emerald-500" /> Secure booking · Verified partner</p>
          </div>
        </div>
      </div>
    </div>
  );
}
